import type { MetricsSeriesPoint } from '@/features/metrics/metricsHelpers';

export type LineChartDatum = { [key: string]: number | string; label: string };

export const normalizeChartLabel = (label: string): string | null => {
  const trimmed = label.trim();
  if (trimmed.length >= 10 && trimmed.includes('-')) {
    return trimmed.slice(0, 10);
  }
  if (trimmed.length === 10 && trimmed.includes('/')) {
    const [day, month, year] = trimmed.split('/');
    if (day && month && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }
  return null;
};

export const buildChartDates = (labels: string[], dates: string[]): string[] => {
  if (labels.length === 0 && dates.length === 0) return [];
  if (labels.length === 0) return dates;
  return labels.map((label, index) => {
    const normalized = normalizeChartLabel(label);
    if (normalized) return normalized;
    if (dates.length > index) return dates[index];
    return label;
  });
};

export const buildLineChartSeries = (
  series: MetricsSeriesPoint[] | undefined,
  labels: string[],
  dates: string[],
  key: string,
): LineChartDatum[] | null => {
  if (labels.length === 0) return null;
  const valuesByLabel = new Map<string, number>();
  series?.forEach((point) => {
    valuesByLabel.set(point.label, point.value);
    const normalized = normalizeChartLabel(point.label);
    if (normalized) {
      valuesByLabel.set(normalized, point.value);
    }
  });
  return labels.map((label, index) => {
    const dateKey = dates[index];
    const valueByLabel = valuesByLabel.get(label);
    if (typeof valueByLabel === 'number') {
      return { label, [key]: valueByLabel };
    }
    const normalizedLabel = normalizeChartLabel(label);
    if (normalizedLabel) {
      const valueByNormalizedLabel = valuesByLabel.get(normalizedLabel);
      if (typeof valueByNormalizedLabel === 'number') {
        return { label, [key]: valueByNormalizedLabel };
      }
    }
    if (dateKey) {
      const normalizedDateKey = normalizeChartLabel(dateKey) ?? dateKey.slice(0, 10);
      const valueByDate = valuesByLabel.get(dateKey);
      if (typeof valueByDate === 'number') {
        return { label, [key]: valueByDate };
      }
      const valueByNormalizedDate = valuesByLabel.get(normalizedDateKey);
      if (typeof valueByNormalizedDate === 'number') {
        return { label, [key]: valueByNormalizedDate };
      }
    }
    if (series && series.length === labels.length) {
      const seriesValue = series[index]?.value;
      if (typeof seriesValue === 'number') {
        return { label, [key]: seriesValue };
      }
    }
    return { label, [key]: 0 };
  });
};
